import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import BookingSummary from "./BookingSummary";

/**
 * Mobile sticky bottom booking bar + expanding summary sheet.
 * Used on both the home configurator and the booking page.
 */
export const BookingBar = ({ services, fonsterputsLevel, mellanFonstren, onRemoveMellan, onContinue, continueLabel = "Fortsätt" }) => {
  const [sheet, setSheet] = useState(false);

  // Reserve space at the bottom of the page so the fixed bar never covers content
  useEffect(() => {
    if (services.length === 0) return;
    document.body.style.paddingBottom = "calc(72px + env(safe-area-inset-bottom))";
    return () => { document.body.style.paddingBottom = ""; };
  }, [services.length]);

  if (services.length === 0) return null;

  return (
    <>
      <AnimatePresence>
        {!sheet && (
          <motion.div
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 80 }}
            transition={{ type: "spring", stiffness: 400, damping: 34 }}
            className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-brand-ink text-white px-5 pt-3.5 pb-[calc(0.875rem+env(safe-area-inset-bottom))] flex items-center justify-between gap-3 shadow-[0_-8px_30px_rgba(7,22,44,0.3)]"
            data-testid="mobile-booking-bar"
          >
            <button
              type="button"
              onClick={() => setSheet(true)}
              data-testid="mobile-summary-open"
              className="text-left"
            >
              <span className="block text-[11px] uppercase tracking-widest text-white/50 font-semibold">Din bokning</span>
              <span className="block text-sm font-semibold">
                {fonsterputsLevel || `${services.length} ${services.length === 1 ? "tjänst" : "tjänster"}`}
                {mellanFonstren && <span className="text-brand-ice"> · + Mellan fönstren</span>}
              </span>
            </button>
            {onContinue && (
              <button
                type="button"
                onClick={onContinue}
                data-testid="mobile-booking-continue"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-blue text-white text-sm font-semibold px-5 py-2.5 shrink-0"
              >
                {continueLabel} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {sheet && (
          <motion.div className="lg:hidden fixed inset-0 z-[60]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-brand-ink/45 backdrop-blur-sm" onClick={() => setSheet(false)} data-testid="mobile-summary-backdrop" />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 36 }}
              className="absolute bottom-0 inset-x-0 bg-white rounded-t-3xl p-6 pb-[calc(2rem+env(safe-area-inset-bottom))] max-h-[70vh] overflow-y-auto"
              data-testid="mobile-summary-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Bokningssammanfattning"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-base font-bold text-brand-ink">Sammanfattning</span>
                <button
                  onClick={() => setSheet(false)}
                  aria-label="Stäng sammanfattning"
                  data-testid="mobile-summary-close"
                  className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-slate-600" />
                </button>
              </div>
              <BookingSummary
                services={services}
                fonsterputsLevel={fonsterputsLevel}
                mellanFonstren={mellanFonstren}
                onRemoveMellan={onRemoveMellan}
              />
              {onContinue && (
                <button
                  type="button"
                  onClick={onContinue}
                  data-testid="mobile-sheet-continue"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-6 py-4"
                >
                  {continueLabel} <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BookingBar;
